/**
 * Canonical hooks for task group queries.
 * Replaces inline gql in TaskGroupListPage + TaskGroupDetailPage.
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const LIST_TASK_GROUPS = gql`
  query ListTaskGroups($subgraph: String, $setting: String, $category: TaskGroupCategory) {
    taskGroups(subgraph: $subgraph, setting: $setting, category: $category) {
      id
      configId
      name
      description
      category
      setting
      subgraph
      isLifeCritical
      completionStrategy
      color
      icon
      tags
      children {
        id
        name
      }
    }
  }
`;

const GET_TASK_GROUP = gql`
  query GetTaskGroup($groupId: String!, $subgraph: String, $setting: String) {
    taskGroup(groupId: $groupId, subgraph: $subgraph, setting: $setting) {
      id
      configId
      name
      description
      category
      setting
      subgraph
      isLifeCritical
      completionStrategy
      color
      icon
      tags
      children {
        id
        name
        estimatedDurationMinutes
        assigneeRole
        priority
        required
        sequenceOrder
        isLifeCritical
        note
        competencyArea
        verification
        requiresDelegation
        waivableWith
        requiredRole
        assessment
        sourceDocument
        deadlineHoursFromHire
      }
    }
  }
`;

export interface TaskGroupSummary {
  id: string;
  configId: string;
  name: string;
  description: string;
  category: string;
  setting: string;
  subgraph: string;
  isLifeCritical: boolean;
  completionStrategy: string;
  color: string | null;
  icon: string | null;
  tags: string[];
  children: Array<{ id: string; name: string }>;
}

interface ListTaskGroupsData {
  taskGroups: TaskGroupSummary[];
}

interface ListTaskGroupsOptions {
  subgraph?: string;
  setting?: string;
  category?: string;
}

export function useTaskGroupList(options: ListTaskGroupsOptions = {}) {
  const { data, loading, error, refetch } = useQuery<ListTaskGroupsData>(
    LIST_TASK_GROUPS,
    {
      variables: {
        subgraph: options.subgraph || undefined,
        setting: options.setting || undefined,
        category: options.category || undefined,
      },
    }
  );

  return {
    taskGroups: data?.taskGroups ?? [],
    loading,
    error,
    refetch,
  };
}

export interface TaskGroupDetail extends TaskGroupSummary {
  children: Array<{
    id: string;
    name: string;
    estimatedDurationMinutes?: number | null;
    assigneeRole?: string | null;
    priority: string;
    required: boolean;
    sequenceOrder: number;
    isLifeCritical: boolean;
    note?: string | null;
    competencyArea?: string | null;
    verification?: string | null;
    requiresDelegation?: boolean;
    waivableWith?: string | null;
    requiredRole?: string[];
    assessment?: string | null;
    sourceDocument?: string | null;
    deadlineHoursFromHire?: number | null;
  }>;
}

interface GetTaskGroupData {
  taskGroup: TaskGroupDetail;
}

export function useTaskGroupDetail(groupId: string, options: { subgraph?: string; setting?: string } = {}) {
  const { data, loading, error, refetch } = useQuery<GetTaskGroupData>(
    GET_TASK_GROUP,
    {
      variables: {
        groupId,
        subgraph: options.subgraph || undefined,
        setting: options.setting || undefined,
      },
      skip: !groupId,
    }
  );

  return {
    taskGroup: data?.taskGroup ?? null,
    loading,
    error,
    refetch,
  };
}
